/**
 * Versicherer — T1 List-View (P2 hardening 02).
 *
 * Backed by the `versicherer` tenant table: the list now shows policy-less and inaktiv insurers,
 * with Ort + the Handelsregister-Abgleich state. Views: Alle / Mit Policen / Ohne Policen / Inaktiv
 * / Renewal steht an. Header action «Neuer Versicherer» (crm.write) opens the create dialog.
 *
 * [Source: docs/stories/v10-j2-web-wiring-rest.md AC4] (the pre-entity list)
 */
import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { ListView } from '../../components/data/ListView';
import { DataTable } from '../../components/data/DataTable';
import { TableCell } from '../../components/ui/Table';
import { EntityLink } from '../../components/ui/EntityLink';
import { StatusDot } from '../../components/ui/StatusDot';
import { Button } from '../../components/ui/Button';
import { chf } from '../../lib/format';
import { isLiveData } from '../../lib/data-mode';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';
import { CarrierTile } from './components/CarrierTile';
import { NeuerVersichererDialog } from './components/NeuerVersichererDialog';
import { useCarrierRows } from './hooks/useCarrierRows';
import type { Carrier } from '../entity-detail/types';

const num = 'text-right mono-num';

/** The Handelsregister-Abgleich chip label (reuses the FirmenStammdaten vocabulary). */
const ABGLEICH_LABEL: Record<Carrier['zefixStatus'], string> = {
  done: 'Handelsregister ✓',
  pending: 'ausstehend',
  in_progress: 'läuft',
  failed: 'fehlgeschlagen',
};

function renewalSoon(c: Carrier): boolean {
  const t = parseInt(c.nextRenewal.replace(/\D/g, ''), 10);
  return !isNaN(t) && t <= 7 && /Tag/.test(c.nextRenewal);
}

export function CarriersScreen() {
  const navigate = useNavigate();
  const { data = [], isLoading, isError } = useCarrierRows();
  const perms = useEffectivePermissions();
  const canManage = perms.can('crm.write');
  const [activeView, setActiveView] = useState(0);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  const views = [
    { label: 'Alle', count: data.length },
    { label: 'Mit Policen', count: data.filter((c) => c.policen > 0).length },
    { label: 'Ohne Policen', count: data.filter((c) => c.policen === 0).length },
    { label: 'Inaktiv', count: data.filter((c) => c.status === 'inaktiv').length },
    { label: 'Renewal steht an', count: data.filter(renewalSoon).length },
  ];

  const filtered = useMemo(() => {
    let rows = data;
    if (activeView === 1) rows = rows.filter((c) => c.policen > 0);
    else if (activeView === 2) rows = rows.filter((c) => c.policen === 0);
    else if (activeView === 3) rows = rows.filter((c) => c.status === 'inaktiv');
    else if (activeView === 4) rows = rows.filter(renewalSoon);
    const q = search.trim().toLowerCase();
    if (q !== '') rows = rows.filter((c) => c.name.toLowerCase().includes(q) || c.id.toLowerCase().includes(q));
    return rows;
  }, [data, activeView, search]);

  const totalVolumen = filtered.reduce((acc, c) => acc + c.volumen, 0);

  const columns: ColumnDef<Carrier>[] = [
    {
      accessorKey: 'name',
      header: 'Versicherer',
      cell: ({ row }) => (
        <div className="flex items-center gap-2.5">
          <CarrierTile name={row.original.name} />
          <EntityLink
            onActivate={() => void navigate({ to: `/versicherer/${row.original.id}` as never })}
            className="font-medium"
          >
            {row.original.name}
          </EntityLink>
          {row.original.status === 'inaktiv' && <StatusDot tone="neutral" label="Inaktiv" />}
        </div>
      ),
    },
    {
      accessorKey: 'city',
      header: 'Ort',
      cell: ({ row }) => <span className="text-muted-foreground">{row.original.city ?? '-'}</span>,
    },
    { accessorKey: 'policen', header: 'Policen', cell: ({ row }) => <div className={num}>{row.original.policen}</div> },
    { accessorKey: 'volumen', header: 'Prämienvolumen', cell: ({ row }) => <div className={num}>{chf(row.original.volumen)}</div> },
    {
      accessorKey: 'nextRenewal',
      header: 'Nächstes Renewal',
      cell: ({ row }) => <span className="text-muted-foreground">{row.original.nextRenewal}</span>,
    },
    {
      accessorKey: 'zefixStatus',
      header: 'Abgleich',
      cell: ({ row }) => <span className="text-xs text-muted-foreground">{ABGLEICH_LABEL[row.original.zefixStatus]}</span>,
    },
  ];

  return (
    <>
      <ListView
        title="Versicherer"
        views={views}
        count={`${filtered.length} von ${data.length}`}
        searchPlaceholder="Versicherer, UID …"
        searchValue={search}
        onSearchChange={setSearch}
        activeView={activeView}
        onViewChange={setActiveView}
        rightAction={
          canManage && isLiveData() ? (
            <Button size="sm" onClick={() => setDialogOpen(true)}>
              <Plus className="size-4" /> Neuer Versicherer
            </Button>
          ) : undefined
        }
      >
        {isLoading ? (
          <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">Lade Versicherer …</div>
        ) : isError ? (
          <div role="alert" className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
            Versicherer konnten nicht geladen werden.
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={filtered}
            onRowClick={(r) => void navigate({ to: `/versicherer/${r.id}` as never })}
            summary={
              <>
                <TableCell className="font-medium">Summe</TableCell>
                <TableCell />
                <TableCell />
                <TableCell className={`font-medium ${num}`}>{chf(totalVolumen)}</TableCell>
                <TableCell />
                <TableCell />
              </>
            }
          />
        )}
      </ListView>
      {dialogOpen && <NeuerVersichererDialog onClose={() => setDialogOpen(false)} />}
    </>
  );
}
